import React, { Fragment } from 'react';
import AbstractToolboxItem from './AbstractToolboxItem';
import Typography from '@mui/material/Typography';
import withBoundary from 'component/ErrorBoundary/withBoundary';

/**
 * Web implementation of {@code AbstractToolboxItem}.
 */
class ToolboxItem extends AbstractToolboxItem {
  /**
   * Initializes a new {@code ToolboxItem} instance.
   *
   * @inheritdoc
   */
  constructor(props) {
    super(props);

    this._onKeyPress = this._onKeyPress.bind(this);
  }

  /**
   * Handles 'Enter' and Space key on the button to trigger onClick for accessibility.
   *
   * @param {Object} event - The key event.
   * @private
   * @returns {void}
   */
  _onKeyPress(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.props.onClick();
    }
  }

  /**
   * Handles rendering of the actual item. If the label is being shown, which
   * is controlled with the `showLabel` prop, the item is rendered for its
   * display in an overflow menu, otherwise it will only have an icon, which
   * can be displayed on any toolbar.
   *
   * @protected
   * @returns {ReactElement}
   */
  _renderItem() {
    const {
      id,
      type,
      disabled,
      elementAfter,
      onClick,
      onKeyDown,
      showLabel,
      toggled,
      isDesktop,
      transparent
    } = this.props;
    const className = showLabel ? 'overflow-menu-item' : 'toolbox-button';
    let classType = type;

    if (classType === 'green' && !toggled) {
      classType = 'red';
    }
    const props = {
      id,
      'aria-pressed': toggled,
      'aria-disabled': disabled,
      'aria-hidden': true,
      key: 'tooltip-anchor',
      autoFocus: true,
      className:
        'vw-button ' +
        className +
        (type ? ` ${classType}` : '') +
        (transparent ? ` transparent` : '') +
        (disabled ? ' disabled' : '') +
        (isDesktop ? ' desktop' : ''),
      onClick: disabled ? undefined : onClick,
      onKeyDown: disabled ? undefined : onKeyDown,
      onKeyPress: this._onKeyPress,
      tabIndex: 1,
      role: showLabel ? 'menuitem' : 'button'
    };

    const elementType = showLabel ? 'li' : 'div';
    const useTooltip = this.tooltip && this.tooltip.length > 0;

    // if (contextMenu) {
    //     return (<ContextMenuItem
    //         accessibilityLabel = { this.accessibilityLabel }
    //         disabled = { disabled }
    //         icon = { icon }
    //         onClick = { onClick }
    //         onKeyDown = { onKeyDown }
    //         onKeyPress = { this._onKeyPress }
    //         text = { this.label } />);
    // }
    let children = (
      <Fragment>
        {this._renderIcon()}
        {showLabel && (
          <Typography variant="labelSmall">{this.label}</Typography>
        )}
        {elementAfter}
      </Fragment>
    );

    if (isDesktop && useTooltip) {
      children = (
        <>
          {children}
          <div className="tooltip-parent">
            <div className="tooltip">
              {this.tooltip}
              <div className="tooltip-arrow"></div>
            </div>
          </div>
        </>
      );
    }

    return React.createElement(elementType, props, children);
  }

  /**
   * Helper function to render the item's icon.
   *
   * @private
   * @returns {ReactElement}
   */
  _renderIcon() {
    const {
      customClass,
      disabled,
      icon: Icon,
      showLabel,
      toggled
    } = this.props;
    const iconComponent = (
      <Icon
        alt="icon"
        width={showLabel ? undefined : 24}
        height={showLabel ? undefined : 24}
        fill="white"
      />
    );
    const elementType = showLabel ? 'span' : 'div';
    const className = `${
      showLabel ? 'overflow-menu-item-icon' : 'toolbox-icon'
    } ${toggled ? 'toggled' : ''} ${disabled ? 'disabled' : ''} ${
      customClass ?? ''
    }`;

    return React.createElement(elementType, { className }, iconComponent);
  }
}
export default React.memo(withBoundary(ToolboxItem));
